/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ClassUtil from '../../ClassUtil';
import CLog, { LogTypes } from '../../ComponentLogger';
import TypeStrings from '../../TypeStrings';
import ReferencedComponent from './ReferencedComponent';
import { ReferenceGroup } from './ReferenceGroup';

/**
 * Static class designed to store and retrieve components for injection.
 */
@cc._decorator.ccclass
export default class ComponentReference
{
    // FIXME : Recombine back into a single reference map, ideally ReferenceComponents should
    // de-register themselves in OnDestroy().

    /** Map of context keys to [[ReferencedComponent]]. */
    protected static localReferenceMap: Map<string, ReferencedComponent> = new Map<string, ReferencedComponent>();
    protected static globalReferenceMap: Map<string, ReferencedComponent> = new Map<string, ReferencedComponent>();

    /**
     * Fetches a [[ReferencedComponent]] based on criteria.
     * @param c Constructor or ContextKey for [[ReferencedComponent]].
     * @param group [[ReferenceGroup]] of [[ReferencedComponent]]
     * @param instanceId [[InstanceID]] of particular [[ReferencedComponent]].
     * @returns [[ReferencedComponent]]
     */
    public static Get<T extends ReferencedComponent>(
        c: { new(): T } | string,
        group: ReferenceGroup,
        instanceId: string = null): T
    {
        let constructorName: string = typeof(c) === TypeStrings.string
            ? c as string
            : ClassUtil.getName(c as { new(): T });

        let key: string = this.GetContextKey(constructorName, group, instanceId);

        CLog.log(`Fetching reference (static): [ ${key} ]`, LogTypes.Utility, null);

        return this.GetByContextKey(key);
    }

    /**
     * Fetches a [[ReferencedComponent]] without providing context.
     * @param instanceId [[InstanceID]] of particular [[ReferencedComponent]].
     * @param group [[ReferenceGroup]] of [[ReferencedComponent]]
     * @returns [[ReferencedComponent]]
     */
    public static GetByInstanceID<T extends ReferencedComponent>(
        instanceID: string,
        referenceGroup: ReferenceGroup = null): T[]
    {
        if (referenceGroup) { instanceID = `${ReferenceGroup[referenceGroup]}.${instanceID}`; }

        let foundComponents: T[] = [];

        this.localReferenceMap.forEach(
            (component: T, key: string) =>
            {
                if (key.endsWith(instanceID)) { foundComponents.push(component); }
            });

        this.globalReferenceMap.forEach(
            (component: T, key: string) =>
            {
                if (key.endsWith(instanceID)) { foundComponents.push(component); }
            });

        return foundComponents;
    }

    /**
     * Fetch [[ReferencedComponent]] directly by context key.
     * @param contextKey ContextKey of requested [[ReferencedComponent]].
     * @returns Matched [[ReferencedComponent]].
     */
    public static GetByContextKey<T extends ReferencedComponent>(contextKey: string): T
    {
        CLog.log(`Fetching reference (static): [ ${contextKey} ]`, LogTypes.Utility, null);

        const localRet: T = this.localReferenceMap.get(contextKey) as T || null;
        return localRet ? localRet : this.globalReferenceMap.get(contextKey) as T || null;
    }

    /**
     * Adds a [[ReferencedComponent]] to the mapping.
     * @param component [[ReferencedComponent]] to add.
     * @param referenceGroup The reference group it belongs to.
     * @param instanceID Instance id for multiple with same context.
     */
    public static AddComponentToMap(
        component: ReferencedComponent,
        referenceGroup: ReferenceGroup,
        instanceID: string = null,
        global: boolean = false): void
    {
        let componentName: string = ClassUtil.getName(component.constructor);
        let contextKey: string = this.GetContextKey(componentName, referenceGroup, instanceID);

        CLog.log(`New reference added: [ ${contextKey} ]`, LogTypes.Utility, null);

        // tslint:disable:max-line-length
        if (global)
        {
            if (this.globalReferenceMap.has(contextKey))
            {
                CLog.warn(`Component [ ${componentName || "uknown" } ] with key [ ${contextKey} ] already added globally.`,
                    LogTypes.Initialization);
            }
            this.globalReferenceMap.set(contextKey, component);
        }
        else
        {
            if (!global && this.localReferenceMap.has(contextKey))
            {
                CLog.warn(`Component [ ${componentName || "uknown" } ] with key [ ${contextKey} ] already added locally.`,
                    LogTypes.Initialization);
            }

            this.localReferenceMap.set(contextKey, component);
        }
        // tslint:enable:max-line-length
    }

    /**
     * Retrieves a unique string key for a particular instance
     * of a [[ReferencedComponent]].
     * @param componentName Name of the component context.
     * @param referenceGroup Group to which [[ReferencedComponent]] belongs.
     * @param instanceId String id of particular instance of a [[ReferencedComponent]].
     */
    public static GetContextKey(
        componentName: string,
        referenceGroup: ReferenceGroup,
        instanceId?: string): string
    {
        componentName = this.objectMappings.get(componentName) || componentName;
        instanceId = instanceId || 'only';

        let referenceGroupName: string = referenceGroup
            ? ReferenceGroup[referenceGroup]
            : ReferenceGroup[ReferenceGroup.MainGame];

        return `${componentName}.${referenceGroupName}.${instanceId}`;
    }

    public static PurgeLocalReferences(): void
    {
        this.localReferenceMap.clear();
    }

    // #region Injection

    /**
     * Map for setting context of specific inheritied class.
     * ie PaylineManager -> Video or Mech.
     */
    protected static objectMappings: Map<string, string> = new Map<string, string>();

    /**
     * Adds reference of one context key to another.
     * @param fromValue From context.
     * @param toValue To context.
     */
    public static MapComponent(fromValue: string | { name: string }, toValue: { new (): cc.Component }): void
    {
        if (typeof(fromValue) !== TypeStrings.string) { fromValue = ClassUtil.getName(fromValue); }
        this.objectMappings.set(fromValue as string, ClassUtil.getName(toValue));
    }

    // #endregion
}
